import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  Download, 
  Share2, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Shield,
  BookOpen,
  MessageCircle,
  Target,
  Zap,
  Users,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Brain,
  Heart,
  Smartphone
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const EduzyPresentation = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const slideRefs = useRef([]);

  const slides = [
    // Slide 1: Cover
    {
      id: 'cover',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex flex-col items-center justify-center p-12 text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-3xl flex items-center justify-center mb-8 shadow-2xl shadow-emerald-500/30">
            <GraduationCap className="w-14 h-14 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">Eduzy</h1>
          <p className="text-2xl text-emerald-400 mb-8">India's First WhatsApp-Based AI Tutor</p>
          <div className="w-32 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full mb-8"></div>
          <p className="text-xl text-slate-300">For Classes 5-8</p>
          <p className="text-lg text-slate-400 mt-4">Quality Education on WhatsApp</p>
        </div>
      )
    },
    // Slide 2: The Problem
    {
      id: 'problem',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-slate-900 to-red-950/30 flex flex-col p-12">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
            <AlertTriangle className="w-10 h-10 text-red-400" />
            The Hidden Crisis
          </h2>
          <div className="grid grid-cols-2 gap-6 flex-1">
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <div className="text-5xl font-bold text-white mb-2">25 Cr</div>
              <div className="text-slate-400">Students in India</div>
            </div>
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <div className="text-5xl font-bold text-red-400 mb-2">65L+</div>
              <div className="text-slate-400">Dropouts in 5 Years</div>
            </div>
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <div className="text-5xl font-bold text-orange-400 mb-2">8-12%</div>
              <div className="text-slate-400">Secondary Dropout Rate</div>
            </div>
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <div className="text-5xl font-bold text-yellow-400 mb-2">79%</div>
              <div className="text-slate-400">Class 9-10 Enrolment</div>
            </div>
          </div>
          <p className="text-lg text-slate-300 mt-6 text-center">
            Classes 5-8 are where learning gaps turn into permanent dropouts
          </p>
        </div>
      )
    },
    // Slide 3: Challenges
    {
      id: 'challenges',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col p-12">
          <h2 className="text-3xl font-bold text-white mb-8">What Children Face in Middle School</h2>
          <div className="grid grid-cols-2 gap-4 flex-1">
            {[
              { title: 'Learning Gap Explosion', desc: 'Weak foundation meets harder subjects', color: 'text-red-400' },
              { title: 'Curriculum Shock', desc: 'Activity-based to subject-heavy shift', color: 'text-orange-400' },
              { title: 'Silent Dropouts', desc: 'Present but not learning', color: 'text-yellow-400' },
              { title: 'Teacher Shortage', desc: 'Rote learning, low engagement', color: 'text-purple-400' },
              { title: 'No Personal Attention', desc: 'Lost in crowded classrooms', color: 'text-blue-400' },
              { title: 'Fear of Questions', desc: 'Embarrassed to ask doubts', color: 'text-pink-400' }
            ].map((item, i) => (
              <div key={item.title || `problem-${i}`} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <h3 className={`font-semibold ${item.color} mb-1`}>{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Slide 4: Solution - Eduzy
    {
      id: 'solution',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-emerald-950 to-slate-900 flex flex-col p-12">
          <h2 className="text-3xl font-bold text-white mb-2">The Solution</h2>
          <p className="text-emerald-400 text-xl mb-8">AI-Powered Learning on WhatsApp</p>
          <div className="flex-1 flex items-center justify-center gap-8">
            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-green-500 to-green-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                <Smartphone className="w-16 h-16 text-white" />
              </div>
              <p className="text-white text-xl font-semibold">WhatsApp</p>
              <p className="text-slate-400">No new app needed</p>
            </div>
            <div className="text-4xl text-emerald-400">+</div>
            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                <Brain className="w-16 h-16 text-white" />
              </div>
              <p className="text-white text-xl font-semibold">AI Tutor</p>
              <p className="text-slate-400">Adapts to your child</p>
            </div>
            <div className="text-4xl text-emerald-400">=</div>
            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                <GraduationCap className="w-16 h-16 text-white" />
              </div>
              <p className="text-white text-xl font-semibold">Better Learning</p>
              <p className="text-slate-400">Anytime, anywhere</p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 5: For Working Parents
    {
      id: 'parents',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-slate-900 to-emerald-950/50 flex flex-col p-12">
          <div className="flex items-center gap-3 mb-6">
            <Heart className="w-8 h-8 text-pink-400" />
            <h2 className="text-3xl font-bold text-white">For Working Parents</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 flex-1">
            {[
              { icon: Clock, title: 'No Travel Required', desc: 'Save 2+ hours daily. Learning at home.', color: 'from-blue-500 to-cyan-500' },
              { icon: Shield, title: 'Quality Maintained', desc: 'Same excellence at 10 AM or 10 PM.', color: 'from-emerald-500 to-green-500' },
              { icon: BookOpen, title: 'Reading Over Scrolling', desc: 'Productive screen time that builds knowledge.', color: 'from-purple-500 to-pink-500' },
              { icon: MessageCircle, title: 'Challenge AI Anytime', desc: 'Ask "why?" unlimited times. No judgment.', color: 'from-orange-500 to-red-500' },
              { icon: Target, title: 'Tailormade Delivery', desc: 'Your child controls the pace.', color: 'from-cyan-500 to-blue-500' },
              { icon: Zap, title: 'Real-Time Progress', desc: 'Stay informed without hovering.', color: 'from-yellow-500 to-orange-500' }
            ].map((item, i) => (
              <div key={item.title || `benefit-${i}`} className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50 flex items-start gap-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Slide 6: Comparison
    {
      id: 'comparison',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col p-12">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Traditional Tuition vs Eduzy</h2>
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700">
              <div className="grid grid-cols-3 bg-slate-900 p-4">
                <div className="text-slate-400 font-medium">Feature</div>
                <div className="text-red-400 font-medium text-center">Traditional</div>
                <div className="text-emerald-400 font-medium text-center">Eduzy</div>
              </div>
              {[
                ['Timing', 'Fixed batch times', 'Learn anytime'],
                ['Teaching', 'One-size-fits-all', 'AI adapts to child'],
                ['Questions', 'Fear of asking', 'Ask unlimited'],
                ['Location', 'Travel to center', 'WhatsApp at home'],
                ['Visibility', 'No parent dashboard', 'Real-time tracking'],
                ['Cost', 'Expensive fees', 'Affordable plans']
              ].map((row, i) => (
                <div key={row[0] || `compare-${i}`} className="grid grid-cols-3 p-4 border-t border-slate-700">
                  <div className="text-white font-medium">{row[0]}</div>
                  <div className="text-slate-400 text-center text-sm">{row[1]}</div>
                  <div className="text-emerald-400 text-center text-sm flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" /> {row[2]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    // Slide 7: How It Works
    {
      id: 'how',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-slate-900 to-emerald-950/30 flex flex-col p-12">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">How Eduzy Works</h2>
          <div className="flex-1 flex items-center justify-center">
            <div className="grid grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Parent Signs Up', desc: 'Add children & subjects', icon: Users },
                { step: '2', title: 'Set Topics', desc: 'Math, Science, custom', icon: Target },
                { step: '3', title: 'Child Learns', desc: 'Daily WhatsApp lessons', icon: Brain },
                { step: '4', title: 'Track Progress', desc: 'See quiz scores & gaps', icon: TrendingDown }
              ].map((item, i) => (
                <div key={item.title || `step-${i}`} className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl font-bold text-white shadow-lg">
                    {item.step}
                  </div>
                  <item.icon className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    // Slide 8: CTA
    {
      id: 'cta',
      content: (
        <div className="w-full h-full bg-gradient-to-br from-emerald-900 via-emerald-950 to-slate-900 flex flex-col items-center justify-center p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-xl">
            <GraduationCap className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Start Your Child's Journey</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-lg">
            Don't wait until learning gaps become permanent. Give your child the support they need today.
          </p>
          <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold text-xl px-10 py-4 rounded-2xl mb-6">
            30-Day Free Trial
          </div>
          <p className="text-emerald-400 text-lg mb-4">Visit: eduzy.co</p>
          <p className="text-slate-400">No credit card required • Cancel anytime</p>
        </div>
      )
    }
  ];

  const downloadPDF = async () => {
    setIsGenerating(true);
    try {
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape A4
      const pageWidth = 297;
      const pageHeight = 210;

      for (let i = 0; i < slides.length; i++) {
        const slideElement = slideRefs.current[i];
        if (!slideElement) continue;

        const canvas = await html2canvas(slideElement, {
          scale: 2,
          backgroundColor: '#0f172a',
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        
        if (i > 0) {
          pdf.addPage();
        }
        
        pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
      }

      pdf.save('Eduzy-Presentation.pdf');
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadSlideAsImage = async (slideIndex) => {
    const slideElement = slideRefs.current[slideIndex];
    if (!slideElement) return;

    try {
      const canvas = await html2canvas(slideElement, {
        scale: 2,
        backgroundColor: '#0f172a'
      });

      const link = document.createElement('a');
      link.download = `Eduzy-Slide-${slideIndex + 1}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Image generation error:', error);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/why-eduzy')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back
            </button>
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold">Eduzy Presentation</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => downloadSlideAsImage(currentSlide)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors text-sm"
            >
              <Share2 className="w-4 h-4" />
              Share Slide
            </button>
            <button
              onClick={downloadPDF}
              disabled={isGenerating}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors text-sm disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {isGenerating ? 'Generating...' : 'Download PDF'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-24 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Slide Preview */}
          <div className="relative aspect-video bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-800 mb-6">
            <div
              ref={(el) => (slideRefs.current[currentSlide] = el)}
              className="absolute inset-0"
            >
              {slides[currentSlide].content}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </button>

            {/* Slide Indicators */}
            <div className="flex items-center gap-2">
              {slides.map((slide, i) => (
                <button
                  key={slide.id || `indicator-${i}`}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    i === currentSlide
                      ? 'bg-emerald-500 w-8'
                      : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Slide Thumbnails */}
          <div className="mt-8 grid grid-cols-4 md:grid-cols-8 gap-3">
            {slides.map((slide, i) => (
              <button
                key={slide.id || `thumb-${i}`}
                onClick={() => setCurrentSlide(i)}
                className={`aspect-video bg-slate-900 rounded-lg overflow-hidden border-2 transition-all ${
                  i === currentSlide ? 'border-emerald-500' : 'border-slate-800 hover:border-slate-600'
                }`}
              >
                <div className="w-full h-full transform scale-[0.15] origin-top-left" style={{ width: '666%', height: '666%' }}>
                  {slide.content}
                </div>
              </button>
            ))}
          </div>

          {/* Instructions */}
          <div className="mt-8 p-6 bg-slate-900/50 border border-slate-800 rounded-xl">
            <h3 className="text-white font-semibold mb-3">How to Share</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-400">
              <div>
                <p className="text-emerald-400 font-medium mb-1">WhatsApp</p>
                <p>Click "Share Slide" to download individual slides as images, then share via WhatsApp</p>
              </div>
              <div>
                <p className="text-emerald-400 font-medium mb-1">Email / Drive</p>
                <p>Click "Download PDF" to get the complete presentation as a PDF file</p>
              </div>
              <div>
                <p className="text-emerald-400 font-medium mb-1">Social Media</p>
                <p>Download slides as images and post on Instagram, Facebook, or LinkedIn</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Hidden slides for PDF generation */}
      <div className="fixed -left-[9999px] -top-[9999px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id || `pdf-slide-${i}`}
            ref={(el) => (slideRefs.current[i] = el)}
            className="w-[1280px] h-[720px]"
          >
            {slide.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EduzyPresentation;
